import React, { useState } from 'react';
import { X, Loader } from 'lucide-react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import pianoLogo from '../../logos/piano-analytics.png';
import config from '../../config';

const API_URL = config.API_URL;

/**
 * Le formulaire de connexion à Piano Analytics : deux clés, et les sites à importer.
 *
 * ## Pourquoi un composant à part
 *
 * Il vivait dans `ConnectorsView`, à l'intérieur d'`App.js`, et n'était donc atteignable que
 * depuis l'écran Connecteurs. Le parcours d'installation devait pouvoir l'ouvrir aussi ; le
 * recopier aurait créé une seconde version à tenir d'accord avec la première, pour un
 * formulaire qui manipule une clé secrète.
 *
 * Le JSX et les gestionnaires sont repris **tels quels**, noms d'états compris : une extraction
 * qui en profite pour réécrire est une extraction dont on ne peut plus dire qu'elle n'a rien
 * changé.
 *
 * ## Ce qu'il crée
 *
 * Comme Matomo et Awin, Piano **crée ses comptes au moment de la connexion** : les sites
 * sélectionnés entrent directement en bibliothèque. Celui qui l'appelle n'a donc pas d'étape de
 * choix de comptes à proposer ensuite.
 *
 * @param {boolean} ouvert
 * @param {() => void} onFermer
 * @param {string} token - JWT de l'utilisateur
 * @param {() => void} onConnecte - après enregistrement réussi
 */
function FormulairePiano({ ouvert, onFermer, token, onConnecte }) {
  const [pianoForm, setPianoForm] = useState({ accessKey: '', secretKey: '', siteIds: '' });
  const [pianoTesting, setPianoTesting] = useState(false);
  const [pianoTestResult, setPianoTestResult] = useState(null);
  const [pianoConnecting, setPianoConnecting] = useState(false);

  const fermer = () => {
    setPianoForm({ accessKey: '', secretKey: '', siteIds: '' });
    setPianoTestResult(null);
    onFermer?.();
  };

  const handlePianoTest = async () => {
    if (!pianoForm.accessKey || !pianoForm.secretKey) {
      setPianoTestResult({ success: false, message: 'Veuillez remplir Access Key et Secret Key' });
      return;
    }

    if (!pianoForm.siteIds || !pianoForm.siteIds.trim()) {
      setPianoTestResult({ success: false, message: 'Veuillez entrer au moins un Site ID' });
      return;
    }

    setPianoTesting(true);
    setPianoTestResult(null);

    try {
      const response = await axios.post(`${API_URL}/api/auth/piano/test`, {
        accessKey: pianoForm.accessKey,
        secretKey: pianoForm.secretKey,
        siteIds: pianoForm.siteIds
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setPianoTestResult({
          success: true,
          message: response.data.message,
          sites: response.data.sites,
          sitesCount: response.data.sitesCount
        });
      } else {
        setPianoTestResult({
          success: false,
          message: response.data.error || 'Échec du test'
        });
      }
    } catch (error) {
      console.error('❌ Erreur test Piano:', error);
      setPianoTestResult({
        success: false,
        message: error.response?.data?.error || error.message || 'Erreur lors du test'
      });
    } finally {
      setPianoTesting(false);
    }
  };

  const handlePianoConnect = async () => {
    if (!pianoTestResult?.success) {
      alert('Veuillez d\'abord tester la connexion');
      return;
    }

    setPianoConnecting(true);

    try {
      const response = await axios.post(`${API_URL}/api/auth/piano/connect`, {
        accessKey: pianoForm.accessKey,
        secretKey: pianoForm.secretKey,
        sites: pianoTestResult.sites // Envoyer les sites validés du test
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        // Les sites fraîchement importés sont relus ici — le composant sait quand ils
        // existent — mais c'est l'appelant qui décide quoi en faire : lui seul connaît la
        // barre latérale et les écrans à rafraîchir.
        let sites = [];
        try {
          const pianoSitesResponse = await axios.get(`${API_URL}/api/dashboard/piano/sites`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (pianoSitesResponse.data.success && pianoSitesResponse.data.sites) {
            sites = pianoSitesResponse.data.sites;
            console.log('🎹 [Piano] Sites rechargés après connexion:', sites.length);
          }
        } catch (pianoErr) {
          console.log('⚠️ [Piano] Erreur rechargement sites après connexion');
        }

        fermer();
        onConnecte?.({ sites });

        alert(`✅ ${response.data.message}`);
      } else {
        alert('❌ Erreur : ' + response.data.error);
      }
    } catch (error) {
      console.error('❌ Erreur connexion Piano:', error);
      alert('❌ Erreur : ' + (error.response?.data?.error || error.message));
    } finally {
      setPianoConnecting(false);
    }
  };


  if (!ouvert) return null;

  return ReactDOM.createPortal(
        <div onClick={fermer} style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: 'white',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '20px 24px',
              borderBottom: '1px solid #e5e0d5'
            }}>
              <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px', fontSize: '1.125rem', fontWeight: '600', color: '#1e293b' }}>
                <img src={pianoLogo} alt="Piano Analytics" style={{width: '24px', height: '24px', borderRadius: '4px'}} />
                Connecter Piano Analytics
              </h3>
              <button onClick={fermer} style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                color: '#64748b',
                borderRadius: '4px'
              }}>
                <X size={20} />
              </button>
            </div>
            <div style={{ padding: '24px' }}>
              <p style={{ marginBottom: '20px', color: '#6b7280', fontSize: '0.875rem' }}>
                Connectez votre compte Piano Analytics (ex-AT Internet) en utilisant vos clés API et vos Site IDs.
                <br />
                <a href="https://developers.piano.io/analytics/data-api/getting-started/authentication/" target="_blank" rel="noopener noreferrer" style={{ color: '#1a1a1a' }}>
                  Comment obtenir les clés API ?
                </a>
              </p>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#374151' }}>
                  Access Key <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="text"
                  value={pianoForm.accessKey}
                  onChange={(e) => setPianoForm({ ...pianoForm, accessKey: e.target.value })}
                  placeholder="Votre Access Key Piano Analytics"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#374151' }}>
                  Secret Key <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="password"
                  value={pianoForm.secretKey}
                  onChange={(e) => setPianoForm({ ...pianoForm, secretKey: e.target.value })}
                  placeholder="Votre Secret Key Piano Analytics"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#374151' }}>
                  Site IDs <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="text"
                  value={pianoForm.siteIds}
                  onChange={(e) => setPianoForm({ ...pianoForm, siteIds: e.target.value })}
                  placeholder="Ex: 628087, 628088, 628089"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    boxSizing: 'border-box'
                  }}
                />
                <small style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '6px', display: 'block' }}>
                  Séparez les IDs par des virgules. Trouvez-les dans Piano Analytics &gt; Settings &gt; Data Collection Portal &gt; Site Management
                </small>
              </div>

              {/* Résultat du test */}
              {pianoTestResult && (
                <div style={{
                  padding: '12px 16px',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  backgroundColor: pianoTestResult.success ? '#f0fdf4' : '#fef2f2',
                  border: `1px solid ${pianoTestResult.success ? '#bbf7d0' : '#fecaca'}`,
                  color: pianoTestResult.success ? '#166534' : '#dc2626'
                }}>
                  <p style={{ margin: 0, fontWeight: '500' }}>
                    {pianoTestResult.success ? '✓ ' : '✕ '}{pianoTestResult.message}
                  </p>
                  {pianoTestResult.success && pianoTestResult.sites && pianoTestResult.sites.length > 0 && (
                    <div style={{ marginTop: '12px', fontSize: '0.875rem' }}>
                      <p style={{ margin: '0 0 8px 0', fontWeight: '500' }}>Sites validés :</p>
                      <ul style={{ margin: 0, paddingLeft: '20px' }}>
                        {pianoTestResult.sites.map(site => (
                          <li key={site.siteId}>{site.name} (ID: {site.siteId})</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={handlePianoTest}
                  disabled={pianoTesting || !pianoForm.accessKey || !pianoForm.secretKey || !pianoForm.siteIds}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    color: '#374151',
                    fontWeight: '500',
                    cursor: pianoTesting || !pianoForm.accessKey || !pianoForm.secretKey || !pianoForm.siteIds ? 'not-allowed' : 'pointer',
                    opacity: pianoTesting || !pianoForm.accessKey || !pianoForm.secretKey || !pianoForm.siteIds ? 0.5 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  {pianoTesting ? (
                    <><Loader className="spin" size={14} /> Test en cours...</>
                  ) : (
                    'Tester la connexion'
                  )}
                </button>
                <button
                  onClick={handlePianoConnect}
                  disabled={pianoConnecting || !pianoTestResult?.success}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    border: 'none',
                    borderRadius: '8px',
                    backgroundColor: pianoConnecting || !pianoTestResult?.success ? '#9ca3af' : '#1a1a1a',
                    color: 'white',
                    fontWeight: '500',
                    cursor: pianoConnecting || !pianoTestResult?.success ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  {pianoConnecting ? (
                    <><Loader className="spin" size={14} /> Connexion...</>
                  ) : (
                    'Connecter'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>,
    document.body
  );
}

export default FormulairePiano;
